import React, { useContext, useEffect, useState } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'

import globalConfirmation from 'live/services/global-confirmation'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { listAlarmGroups } from 'plugin-codaf-risk-ext-bowtie/services/alarms'
import { IAlarmGroup } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

import { EditorTopBar, IError } from '../../../generic'
import { useDimensions } from '../../../hooks'
import { getBowtie, listBarrierTypeIcons, updateBowtie } from '../../../services'
import { clearAllStore, IReduxActions, store } from '../../../store'
import { IBarrier, IBarrierTypeIcon, IBowtie, IConsequence, IReduxStoreReducers, IThreat } from '../../../types'
import { bowtieTheme } from '../../../utils/constants'
import { BowtieSettingsRouteContext, IBowtieSettingsRouteContext, IQueryParamsEdit } from '../../BowtieSettingWrapper'
import { BowtieEditorSVG, BarrierAssignModal, BowtieEditorList, ThreatConsequenceAssignModal } from './components'
import { AlarmGroupListDropdown, BowtieEditorDiv, DivDropdownAlarmGroup, Loading } from './styles'

export type IType = 'threat' | 'consequence'
export type IDiagramTypes = 'THREAT' | 'CONSEQUENCE'
export interface IThreatEditor extends IThreat {
    uid?: string
}

export interface IConsequenceEditor extends IConsequence {
    uid?: string
}
interface ITempItem {
    item: IBarrier
    escId: string
    type: IType
    isThreatConsequenceMandatory: boolean
    isTopEventMandatory: boolean
}

interface ITempThCq {
    item: IThreatEditor | IConsequenceEditor
    type: IType
}

const BowtieEditorComponent: React.FC = () => {
    const dimensions = useDimensions()
    const [barrierTypeIconMap] = useState<Map<number, IBarrierTypeIcon>>(new Map())
    const [loading, setLoading] = useState<boolean>(false)
    const [alarmsGroup, setAlarmGroups] = useState<IAlarmGroup[]>()
    const [showAlarmGroupDropdown, setShowAlarmGroupDropdown] = useState(false)

    const [showThreatConsequenceModal, setShowThreatConsequenceModal] = useState(false)
    const [selectedThreatConsequence, setSelectedThreatConsequence] = useState<ITempThCq>()

    const [bowtieData, setBowtieData] = useState<IBowtie>()
    const [tempItem, setTempItem] = useState<ITempItem>()
    const [showBarrierAssignModal, setShowBarrierAssignModal] = useState(false)
    const [needToUpdate, setNeedToUpdate] = useState(false)
    const [bowtieId, setBowtieId] = useState<string>('')
    const { params, setParams } = useContext<IBowtieSettingsRouteContext<IQueryParamsEdit>>(BowtieSettingsRouteContext)
    const [errors, setErrors] = useState<IError[]>([])

    const [saveLoading, setSaveLoading] = useState(false)

    const dispatch = useDispatch()
    const { consequencesExpandedBarriers, threatsExpandedBarriers } = useSelector(
        (state: IReduxStoreReducers) => state.bowtieExpandedBarriers
    )

    const handleGetBowtieData = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await getBowtie(params.id)
            setBowtieData(data)

            if (data.bowtieCode) setBowtieId(data.bowtieCode)
        } catch (err) {
            globalNotification.error(i18n(err.message ?? 'An error occurred on get bowtie.'))
        } finally {
            setLoading(false)
        }
    }

    const handleFetchBarrierTypeIconsAndStoreIntoDictionary = async (): Promise<void> => {
        const data = await listBarrierTypeIcons()
        data?.data?.forEach(el => {
            const decodedSvg = atob(el.svgImage)
            el.decodedSvgImage = decodedSvg
            barrierTypeIconMap.set(el.id, el)
        })
    }

    const handleEditError = (type: IType, threatConsequenceData: IThreatEditor | IConsequenceEditor): void => {
        for (const [idx, threat] of bowtieData.topEvent.threats.entries()) {
            const newBarriers = threat.barriers?.filter(old => old.id != null)
            bowtieData.topEvent.threats[idx].barriers = newBarriers
        }
        for (const [idx, consequence] of bowtieData.topEvent.consequences.entries()) {
            const newBarriers = consequence.barriers?.filter(old => old.id != null)
            bowtieData.topEvent.consequences[idx].barriers = newBarriers
        }
        handleBowtieChange()
        handleCloseThreatConsequenceModal()

        setTimeout(() => {
            dispatch<IReduxActions['addBarrierEditor']>({
                type: 'SET_ADD_BARRIER_TARGET',
                payload: {
                    newTargetUid: `${type === 'threat' ? 'thr' : 'cnq'}-${threatConsequenceData.id ??
                        threatConsequenceData.uid}`
                }
            })
        }, 100)
    }

    const mountErrorList = (): IError[] => {
        const items: IError[] = []
        if (bowtieData) {
            bowtieData.topEvent.threats.forEach(threat => {
                const threatErrors: string[] = []
                if (threat.barriers.length === 0) {
                    threatErrors.push(i18n('at least one barrier'))
                }

                if (threatErrors.length) {
                    items.push({
                        type: 'THREAT',
                        name: threat.name,
                        item: threat,
                        errors: `${i18n('Missing')} ${threatErrors.join(', ')}`,
                        onEdit: () => handleEditError('threat', threat)
                    })
                }
            })
            bowtieData.topEvent.consequences.forEach(consequence => {
                const consequenceErrors: string[] = []
                if (consequence.barriers.length === 0) {
                    consequenceErrors.push(i18n('at least one barrier'))
                }
                if (consequenceErrors.length) {
                    items.push({
                        type: 'CONSEQUENCE',
                        name: consequence.name,
                        item: consequence,
                        errors: `${i18n('Missing')} ${consequenceErrors.join(', ')}`,
                        onEdit: () => handleEditError('consequence', consequence)
                    })
                }
            })
            bowtieData.topEvent.threats.forEach(threat => {
                const threatFrequencyErrors: string[] = []
                if (threat.initialFrequency == null || threat.initialFrequency < 0 || threat.initialFrequency > 1) {
                    threatFrequencyErrors.push(i18n('a valid threshold frequency'))
                }

                if (threatFrequencyErrors.length) {
                    items.push({
                        type: 'THREAT',
                        name: threat.name,
                        item: threat,
                        errors: `${i18n('Missing')} ${threatFrequencyErrors.join(', ')}`,
                        onEdit: () => handleOpenThreatConsequenceModal(threat, 'threat')
                    })
                }
            })
            bowtieData.topEvent.consequences.forEach(consequence => {
                const consequenceTresholdErrors: string[] = []
                if (
                    consequence.thresholdFrequency == null ||
                    consequence.thresholdFrequency < 0 ||
                    consequence.thresholdFrequency > 1
                ) {
                    consequenceTresholdErrors.push(i18n('a valid threshold frequency'))
                }
                if (consequenceTresholdErrors.length) {
                    items.push({
                        type: 'CONSEQUENCE',
                        name: consequence.name,
                        item: consequence,
                        errors: `${i18n('Missing')} ${consequenceTresholdErrors.join(', ')}`,
                        onEdit: () => handleOpenThreatConsequenceModal(consequence, 'consequence')
                    })
                }
            })
        }

        return items
    }

    const handleBowtieChange = (
        newBarrier?: IBarrier,
        isThreatConsequenceMandatory?: boolean,
        isTopEventMandatory?: boolean
    ): void => {
        const newBowtie = { ...bowtieData }

        if (newBarrier) {
            const rootId = tempItem.escId
                .split('|')
                .shift()
                .replace('thr-', '')
                .replace('cnq-', '')

            const barId = tempItem.escId
                .split('|')
                .pop()
                .replace('bar-', '')

            if (tempItem.type === 'threat') {
                const rootIndex = newBowtie.topEvent.threats.findIndex((thr: IThreatEditor) => {
                    if (rootId?.startsWith('u')) {
                        return thr.uid === rootId
                    } else {
                        return thr.id === Number(rootId)
                    }
                })
                const barrierIndex =
                    barId === 'new'
                        ? 0
                        : newBowtie.topEvent.threats[rootIndex].barriers.findIndex(b => b.id === Number(barId))

                newBowtie.topEvent.threats[rootIndex].barriers[barrierIndex] = newBarrier

                if (isTopEventMandatory !== undefined) {
                    const barrierAlreadyIsMandatory = newBowtie.topEvent.dependentBarriers.findIndex(
                        barrier => barrier.id === newBarrier.id
                    )

                    if (isTopEventMandatory) {
                        if (barrierAlreadyIsMandatory < 0) {
                            newBowtie.topEvent.dependentBarriers = [
                                ...(newBowtie.topEvent.dependentBarriers ?? []),
                                newBarrier
                            ]
                        }
                    } else {
                        if (barrierAlreadyIsMandatory >= 0) {
                            newBowtie.topEvent.dependentBarriers = newBowtie.topEvent.dependentBarriers.filter(
                                (_, idx) => idx !== barrierAlreadyIsMandatory
                            )
                        }
                    }
                }

                if (isThreatConsequenceMandatory !== undefined) {
                    const mandatoryThreatIndex = newBowtie.topEvent.threats[rootIndex].dependentBarriers.findIndex(
                        barrier => barrier.id === newBarrier.id
                    )

                    if (isThreatConsequenceMandatory) {
                        if (mandatoryThreatIndex < 0) {
                            newBowtie.topEvent.threats[rootIndex].dependentBarriers = [
                                ...(newBowtie.topEvent.threats[rootIndex].dependentBarriers ?? []),
                                newBarrier
                            ]
                        }
                    } else {
                        if (mandatoryThreatIndex >= 0) {
                            newBowtie.topEvent.threats[rootIndex].dependentBarriers = newBowtie.topEvent.threats[
                                rootIndex
                            ].dependentBarriers.filter((_, idx) => idx !== mandatoryThreatIndex)
                        }
                    }
                }

                setTempItem(old => ({
                    ...tempItem,
                    isThreatConsequenceMandatory:
                        isThreatConsequenceMandatory !== undefined
                            ? isThreatConsequenceMandatory
                            : old.isThreatConsequenceMandatory,
                    isTopEventMandatory:
                        isTopEventMandatory !== undefined ? isTopEventMandatory : old.isTopEventMandatory,
                    item: newBarrier
                }))
            } else if (tempItem.type === 'consequence') {
                const rootIndex = newBowtie.topEvent.consequences.findIndex((cnq: IConsequenceEditor) => {
                    if (rootId?.startsWith('u')) {
                        return cnq.uid === rootId
                    } else {
                        return cnq.id === Number(rootId)
                    }
                })
                const barrierIndex =
                    barId === 'new'
                        ? 0
                        : newBowtie.topEvent.consequences[rootIndex].barriers.findIndex(b => b.id === Number(barId))
                newBowtie.topEvent.consequences[rootIndex].barriers[barrierIndex] = newBarrier

                if (isThreatConsequenceMandatory !== undefined) {
                    const mandatoryConsequenceIndex = newBowtie.topEvent.consequences[
                        rootIndex
                    ].dependentBarriers.findIndex(barrier => barrier.id === newBarrier.id)

                    if (isThreatConsequenceMandatory) {
                        if (mandatoryConsequenceIndex < 0) {
                            newBowtie.topEvent.consequences[rootIndex].dependentBarriers = [
                                ...(newBowtie.topEvent.consequences[rootIndex].dependentBarriers ?? []),
                                newBarrier
                            ]
                        }
                    } else {
                        if (mandatoryConsequenceIndex >= 0) {
                            newBowtie.topEvent.consequences[
                                rootIndex
                            ].dependentBarriers = newBowtie.topEvent.consequences[rootIndex].dependentBarriers.filter(
                                (_, idx) => idx !== mandatoryConsequenceIndex
                            )
                        }
                    }
                }

                setTempItem(old => ({
                    ...tempItem,
                    isThreatConsequenceMandatory:
                        isThreatConsequenceMandatory !== undefined
                            ? isThreatConsequenceMandatory
                            : old.isThreatConsequenceMandatory,
                    item: newBarrier
                }))
            }
        }
        setBowtieData({ ...bowtieData })
        setNeedToUpdate(true)
    }

    const handleCloseBarrierAssignModal = (): void => {
        setTempItem(null)
        setShowBarrierAssignModal(false)
    }

    const handleOpenThreatConsequenceModal = (item: IThreatEditor | IConsequenceEditor, type: IType): void => {
        handleCloseBarrierAssignModal()
        setSelectedThreatConsequence({ item, type })
        setShowThreatConsequenceModal(true)
    }

    const thCqChange = (data: IBowtie): void => {
        setBowtieData({ ...data })
        setNeedToUpdate(true)
    }

    const handleDeleteThreatConsequenceClick = (type: IType, index: number): void => {
        const newBowtieData = bowtieData
        const oldReference: IConsequenceEditor | IThreatEditor = bowtieData.topEvent[`${type}s`]?.[index]

        if (type === 'threat') {
            newBowtieData.topEvent.threats = newBowtieData.topEvent.threats.filter((_, idx) => idx !== index)
        } else if (type === 'consequence') {
            newBowtieData.topEvent.consequences = newBowtieData.topEvent.consequences.filter((_, idx) => idx !== index)
        }

        if (oldReference) {
            const id = `${type === 'threat' ? 'thr' : 'cnq'}-${oldReference.id ?? oldReference.uid}`
            const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
            const newExpandedBarriers = expandedBarriers.filter(barr => !barr.barrierId.startsWith(id))
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: newExpandedBarriers
                }
            })
        }
        handleBowtieChange()
    }

    const handleCloseThreatConsequenceModal = (): void => {
        if (
            selectedThreatConsequence?.item?.name?.trim().length === 0 &&
            bowtieData.topEvent[`${selectedThreatConsequence.type}s`]?.length > 1
        ) {
            handleDeleteThreatConsequenceClick(
                selectedThreatConsequence.type,
                bowtieData.topEvent[`${selectedThreatConsequence.type}s`]?.indexOf(
                    selectedThreatConsequence.item as IConsequence & IThreat
                )
            )
        }
        setShowThreatConsequenceModal(false)
        setSelectedThreatConsequence(undefined)
    }

    const handleSave = async (): Promise<void> => {
        try {
            setSaveLoading(true)
            for (const consequence of bowtieData.topEvent.consequences) {
                if (consequence.barriers.length === 0) {
                    throw new Error(i18n('Error on save bowtie. Each consequence must have at least one barrier'))
                }
            }
            const dataToUpload = bowtieData

            if (bowtieId) {
                dataToUpload.bowtieCode = bowtieId
            }

            await updateBowtie(dataToUpload)
            globalNotification.success(i18n('Bowtie saved successfully.'))
            setNeedToUpdate(false)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('Error on save bowtie.'))
        } finally {
            setSaveLoading(false)
        }
    }

    const handleShowBarrierAssignModal = (k: IBarrier, type: IType, escId?: string): void => {
        const fatherId = escId
            .split('|')
            .shift()
            .replace('thr-', '')
            .replace('cnq-', '')
        // .replace(/\D/g, '')

        let isThreatConsequenceMandatory = null

        if (type === 'threat') {
            isThreatConsequenceMandatory =
                bowtieData.topEvent.threats
                    .find((thr: IThreatEditor) => {
                        if (fatherId?.startsWith('u')) {
                            return thr.uid === fatherId
                        } else {
                            return thr.id === Number(fatherId)
                        }
                    })
                    .dependentBarriers.find(el => el.id === k.id) ?? null
        } else {
            isThreatConsequenceMandatory =
                bowtieData.topEvent.consequences
                    .find((cnq: IConsequenceEditor) => {
                        if (fatherId?.startsWith('u')) {
                            return cnq.uid === fatherId
                        } else {
                            return cnq.id === Number(fatherId)
                        }
                    })
                    .dependentBarriers.find(el => el.id === k.id) ?? null
        }
        setTempItem({
            item: k,
            type,
            escId: escId ?? null,
            isThreatConsequenceMandatory: isThreatConsequenceMandatory,
            isTopEventMandatory: !!bowtieData.topEvent.dependentBarriers.find(el => el.id === k.id) ?? null
        })

        setShowBarrierAssignModal(true)
    }

    function handleRecursiveSearchAndUpdate(barrier: IBarrier): void {
        bowtieData.topEvent.consequences.forEach(cnq => {
            cnq.barriers.forEach((bar, index) => {
                if (bar.id === barrier.id) {
                    cnq.barriers[index] = { ...barrier }
                }
            })
        })
        bowtieData.topEvent.threats.forEach(thr => {
            thr.barriers.forEach((bar, index) => {
                if (bar.id === barrier.id) {
                    thr.barriers[index] = { ...barrier }
                }
            })
        })
        handleBowtieChange()
    }

    async function getAllAlarmsGroups(): Promise<void> {
        try {
            const resquest = await listAlarmGroups()

            setAlarmGroups(resquest.data)
        } catch (err) {
            console.error(err)
            globalNotification.error(err.message ?? i18n('An error occurred on list alarm groups.'))
        }
    }

    useEffect(() => {
        if (params.id) {
            setBowtieData(undefined)
            clearAllStore()
            handleGetBowtieData()
            getAllAlarmsGroups()
        }
    }, [params.id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => clearAllStore()
    }, [])

    useEffect(() => {
        handleFetchBarrierTypeIconsAndStoreIntoDictionary()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setErrors(mountErrorList())
    }, [bowtieData]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <>
            {loading ? (
                <Loading style={{ height: dimensions.height }}>
                    <i className="fa fa-spinner fa-spin fa-7x" />
                    <p>{i18n('Loading')}</p>
                </Loading>
            ) : (
                <>
                    <EditorTopBar
                        title={[
                            {
                                text: bowtieData?.asset.name,
                                maxWidth: '15%'
                            },
                            {
                                text: bowtieId,
                                onChange: e => {
                                    setNeedToUpdate(true)
                                    setBowtieId(e)
                                },
                                maxLength: 8,
                                maxWidth: '15%'
                            },
                            {
                                text: bowtieData?.topEvent.hazard.name,
                                maxWidth: '27.5%'
                            },
                            {
                                text: bowtieData?.topEvent.name,
                                maxWidth: '27.5%'
                            },
                            {
                                text: bowtieData?.topEvent.hazard.operation.description,
                                maxWidth: '15%'
                            },
                            {
                                element: alarmsGroup?.filter(
                                    alarmGroup =>
                                        alarmGroup.alarmBowties.find(group => group.bowtieId === params.id) != null
                                ).length > 0 && (
                                    <AlarmGroupListDropdown>
                                        <div
                                            className="button-dropdown-notification"
                                            onClick={() => setShowAlarmGroupDropdown(old => !old)}
                                        >
                                            {i18n('Notified Groups')}
                                            <i className={`fas fa-caret-down`}></i>
                                        </div>
                                        <DivDropdownAlarmGroup show={showAlarmGroupDropdown}>
                                            {alarmsGroup
                                                ?.filter(
                                                    alarmGroup =>
                                                        alarmGroup.alarmBowties.find(
                                                            group => group.bowtieId === params.id
                                                        ) != null
                                                )
                                                .map((alarmGroup, idx) => (
                                                    <>
                                                        <div key={idx}>{alarmGroup.groupName}</div>
                                                    </>
                                                ))}
                                        </DivDropdownAlarmGroup>
                                    </AlarmGroupListDropdown>
                                ),
                                maxWidth: '20%'
                            }
                        ]}
                        handleGoBack={() => {
                            if (needToUpdate) {
                                globalConfirmation.warn(
                                    i18n('Are you sure you want to leave without saving your work?'),
                                    () => setParams({ type: 'list', tab: 'bowties' }),
                                    { btnText: i18n('Leave without saving') }
                                )
                            } else {
                                setParams({ type: 'list', tab: 'bowties' })
                            }
                        }}
                        handleSave={handleSave}
                        saveLoading={saveLoading}
                        disabled={!needToUpdate || errors.length > 0}
                    />
                    <BowtieEditorDiv style={{ height: dimensions.height }}>
                        <div className="diagram-template">
                            <BowtieEditorSVG
                                handleDeleteThreatConsequenceClick={handleDeleteThreatConsequenceClick}
                                height={dimensions.height}
                                width={dimensions.width - 410}
                                bowtieData={bowtieData}
                                bowtieCode={bowtieId}
                                onDeleteClick={handleBowtieChange}
                                onEditClick={handleShowBarrierAssignModal}
                                selectedBarrier={tempItem?.item}
                                onEditThreatConsequence={handleOpenThreatConsequenceModal}
                                selectedThreatConsequence={selectedThreatConsequence?.item}
                                handleChange={handleBowtieChange}
                                barrierTypeIconMap={barrierTypeIconMap}
                            />
                        </div>

                        <div className="diagram-list">
                            {bowtieData && (
                                <BowtieEditorList
                                    data={bowtieData}
                                    errors={errors}
                                    handleEditClick={handleShowBarrierAssignModal}
                                    handleDeleteClick={handleBowtieChange}
                                    onEditThreatConsequence={handleOpenThreatConsequenceModal}
                                    handleDeleteThreatConsequenceClick={handleDeleteThreatConsequenceClick}
                                />
                            )}
                        </div>
                        <BarrierAssignModal
                            data={bowtieData}
                            borderColor={bowtieTheme.barrierColors.active}
                            handleChange={handleBowtieChange}
                            handleClose={handleCloseBarrierAssignModal}
                            isOpen={showBarrierAssignModal}
                            item={tempItem?.item}
                            setItem={item => setTempItem(old => ({ ...old, item }))}
                            type={tempItem?.type}
                            escId={tempItem?.escId}
                            isThreatConsequenceMandatory={tempItem?.isThreatConsequenceMandatory}
                            isTopEventMandatory={tempItem?.isTopEventMandatory}
                            title="BARRIER"
                            handleDeleteClick={handleBowtieChange}
                            updateBarriers={handleRecursiveSearchAndUpdate}
                        />
                        <ThreatConsequenceAssignModal
                            borderColor={bowtieTheme[selectedThreatConsequence?.type]}
                            item={selectedThreatConsequence?.item}
                            isOpen={showThreatConsequenceModal}
                            handleClose={handleCloseThreatConsequenceModal}
                            type={selectedThreatConsequence?.type}
                            handleChange={thCqChange}
                            data={bowtieData}
                            setSelected={setSelectedThreatConsequence}
                        />
                    </BowtieEditorDiv>
                </>
            )}
        </>
    )
}

export const BowtieEditor: React.FC = () => {
    return (
        <Provider store={store as any}>
            <BowtieEditorComponent />
        </Provider>
    )
}
